/**
 * Background Sync Service
 *
 * Manages background synchronization of failed requests and pending actions.
 * Queues failed requests for retry when connection is restored.
 * Handles conflict resolution with server state.
 *
 * @author AI Assistant
 * @version 2.0.0
 */
import { offlineDashboardService } from './offlineDashboardService';
import { logger } from "../utils/logger";
import { installTrustedNavigatorServiceWorkerMessageListener } from '../utils/trustedServiceWorkerMessage';
import { K9_OFFLINE_AUTH_V2_ENABLED } from '../config/featureFlags';
// import { logger } from "../utils/logger";
// =============================================================================
// TYPES & INTERFACES
// =============================================================================
export interface SyncRequest {
    id: string;
    url: string;
    method: string;
    headers: Record<string, string>;
    body: any;
    timestamp: number;
    retryCount: number;
    priority: 'high' | 'medium' | 'low';
    type: SyncRequestType;
    /**
     * Identifier of the user who originated this queued mutation. Tagged at
     * queue time so the replay path can verify against the freshly
     * revalidated user on reconnect and refuse to flush mismatched entries
     * (shared device / multi-account scenarios).
     */
    actorUserId?: string | null;
}
export type SyncRequestType = 'form_submission' | 'profile_update' | 'event_registration' | 'notification_read' | 'analytics_event' | 'file_upload' | 'generic';
export interface SyncResult {
    success: boolean;
    syncedCount: number;
    failedCount: number;
    /**
     * Entries that were not flushed because their `actorUserId` did not
     * match the currently authenticated user; they remain in the queue for
     * the original actor to flush when they sign back in.
     */
    heldCount?: number;
    conflicts: ConflictResult[];
}
export interface ConflictResult {
    requestId: string;
    type: string;
    message: string;
    serverState: any;
    localState: any;
}
export type SyncEventType = 'started' | 'progress' | 'completed' | 'failed' | 'conflict';
export interface SyncEventListener {
    (event: SyncEventType, data?: any): void;
}
// =============================================================================
// BACKGROUND SYNC SERVICE
// =============================================================================
class BackgroundSyncService {
    private static instance: BackgroundSyncService;
    private syncInProgress = false;
    private listeners: Set<SyncEventListener> = new Set();
    private autoSyncInterval: NodeJS.Timeout | null = null;
    private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
    /**
     * The id of the currently authenticated user, set by the auth context
     * once `/api/auth/me` resolves successfully (or once an offline
     * snapshot is loaded). Used to:
     *  - tag any new queued mutations with their actor
     *  - filter the replay path so we never POST under the wrong user
     *
     * `null` means no authenticated user is known.
     */
    private currentActorUserId: string | null = null;
    /**
     * When true, internal triggers (online/visibility events) skip
     * auto-syncing and wait for an explicit `sync()` call from the auth
     * context after `/api/auth/me` revalidates. This guarantees the
     * /api/auth/me → flush-queue ordering required by the offline auth
     * model (`sync-reconnect-order`).
     *
     * When `K9_OFFLINE_AUTH_V2` is disabled at build time, this is false
     * so reconnect still flushes the dashboard queue via the legacy
     * `handleOnline` path (the auth context deliberately skips its online
     * listener in that mode).
     */
    private deferAutoSyncUntilAuthVerified = K9_OFFLINE_AUTH_V2_ENABLED;
    private constructor() {
        // Initialize service worker registration
        this.initializeServiceWorker();
        // Listen for online/offline events
        window.addEventListener('online', () => this.handleOnline());
        window.addEventListener('offline', () => this.handleOffline());
        // Listen for visibility change (app becomes visible). We only
        // trigger a sync when the auth context has registered an actor —
        // otherwise we'd race auth bootstrap on a fresh tab open.
        document.addEventListener('visibilitychange', () => {
            if (
                !document.hidden &&
                navigator.onLine &&
                this.currentActorUserId !== null
            ) {
                this.sync({ actorUserId: this.currentActorUserId });
            }
        });
    }
    /**
     * Get singleton instance
     */
    public static getInstance(): BackgroundSyncService {
        if (!BackgroundSyncService.instance) {
            BackgroundSyncService.instance = new BackgroundSyncService();
        }
        return BackgroundSyncService.instance;
    }
    /**
     * Register the id of the currently authenticated user. Auth context
     * calls this whenever the authenticated identity changes (login,
     * `/api/auth/me` revalidation, offline-snapshot load, logout). When
     * `null`, no authenticated user is known and queuing helpers will tag
     * new entries with `null`.
     */
    public setCurrentActorUserId(userId: string | number | null | undefined): void {
        const normalized =
            userId === null || userId === undefined ? null : String(userId);
        if (this.currentActorUserId !== normalized) {
            this.currentActorUserId = normalized;
            logger.debug(
                `🔐 BackgroundSyncService actor set to ${normalized ?? 'null'}`,
            );
        }
    }
    /**
     * Read the currently registered actor user id.
     */
    public getCurrentActorUserId(): string | null {
        return this.currentActorUserId;
    }
    /**
     * Initialize service worker
     */
    private async initializeServiceWorker(): Promise<void> {
        if ('serviceWorker' in navigator) {
            try {
                const registration = await navigator.serviceWorker.ready;
                this.serviceWorkerRegistration = registration;
                installTrustedNavigatorServiceWorkerMessageListener((event: MessageEvent) => {
                    this.handleServiceWorkerMessage(event);
                });
                logger.debug('✅ Background Sync Service initialized with SW');
            }
            catch (error) {
                logger.error('⚠️ Service Worker not available for background sync:', error, {});
            }
        }
    }
    /**
     * Handle messages from service worker
     */
    private handleServiceWorkerMessage(event: MessageEvent): void {
        const { type, data } = event.data;
        switch (type) {
            case 'SYNC_STARTED':
                this.notifyListeners('started');
                break;
            case 'SYNC_COMPLETED':
                this.notifyListeners('completed', data);
                break;
            case 'SYNC_FAILED':
                this.notifyListeners('failed', data);
                break;
            case 'RETRY_REQUEST':
                // Handle retry request from service worker
                this.processRetryRequest(data.requestId);
                break;
        }
    }
    // =============================================================================
    // QUEUE MANAGEMENT
    // =============================================================================
    /**
     * Add request to sync queue
     */
    public async queueRequest(url: string, method: string, body: any, options: {
        priority?: 'high' | 'medium' | 'low';
        type?: SyncRequestType;
        headers?: Record<string, string>;
        /**
         * The id of the user who initiated the action. When present, the
         * replay path verifies this matches the freshly authenticated user
         * before flushing the queue.
         */
        actorUserId?: string | null;
    } = {}): Promise<string> {
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };
        const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // Honor an explicit `actorUserId` override; otherwise stamp the
        // current authenticated user (if any). This makes every new queued
        // entry attributable on replay without callers having to plumb
        // user context through.
        const explicit =
            options.actorUserId === undefined
                ? undefined
                : options.actorUserId === null
                    ? null
                    : String(options.actorUserId);
        const actorUserId =
            explicit !== undefined
                ? (explicit ?? undefined)
                : (this.currentActorUserId ?? undefined);
        await offlineDashboardService.addToSyncQueue(url, method, headers, body, actorUserId);
        logger.api(
            `📥 Queued ${options.type || 'generic'} request: ${method} ${url}` +
                (actorUserId ? ` (actor=${actorUserId})` : ''),
            "GET",
        );
        // Register background sync with service worker if available
        if (this.serviceWorkerRegistration && 'sync' in this.serviceWorkerRegistration) {
            try {
                await this.serviceWorkerRegistration.sync.register(`retry-request-${id}`);
                logger.api('🔄 Registered background sync for request:', id);
            }
            catch (error) {
                logger.error('⚠️ Failed to register background sync:', error, {});
            }
        }
        return id;
    }
    /**
     * Get pending sync requests count
     */
    public async getPendingCount(): Promise<number> {
        const queue = await offlineDashboardService.getSyncQueue();
        return queue.length;
    }
    /**
     * Clear all pending requests
     */
    public async clearQueue(): Promise<void> {
        await offlineDashboardService.clearSyncQueue();
        logger.debug('🗑️ Sync queue cleared');
    }
    // =============================================================================
    // SYNC OPERATIONS
    // =============================================================================
    /**
     * Sync all pending requests
     *
     * @param options.actorUserId - The id of the user that has been
     *   verified as currently authenticated. When provided, queued entries
     *   tagged with a different `actorUserId` are skipped (held) instead of
     *   replayed under the wrong account. Callers who can't supply this
     *   (legacy code paths) can omit it; entries without an `actorUserId`
     *   will continue to flush as before.
     */
    public async sync(options: { actorUserId?: string | null } = {}): Promise<SyncResult> {
        if (this.syncInProgress) {
            logger.debug('⏳ Sync already in progress, skipping');
            return {
                success: false,
                syncedCount: 0,
                failedCount: 0,
                heldCount: 0,
                conflicts: []
            };
        }
        if (!navigator.onLine) {
            logger.debug('📵 Offline, cannot sync');
            return {
                success: false,
                syncedCount: 0,
                failedCount: 0,
                heldCount: 0,
                conflicts: []
            };
        }
        this.syncInProgress = true;
        this.notifyListeners('started');
        try {
            // When callers omit `actorUserId`, use the identity the auth
            // context last registered (covers manual sync, periodic sync, and
            // legacy `sync()` with no args). Explicit `actorUserId` in
            // `options` always wins — reconnect passes the freshly verified id.
            let actorUserId: string | undefined;
            if ('actorUserId' in options) {
                actorUserId =
                    options.actorUserId === null || options.actorUserId === undefined
                        ? undefined
                        : String(options.actorUserId);
            }
            else {
                actorUserId = this.currentActorUserId ?? undefined;
            }
            const result = await offlineDashboardService.processSyncQueue(actorUserId);
            this.notifyListeners('completed', {
                syncedCount: result.success,
                failedCount: result.failed,
                heldCount: result.held,
            });
            // Also sync dashboard data from service worker
            if (this.serviceWorkerRegistration && 'sync' in this.serviceWorkerRegistration) {
                try {
                    await this.serviceWorkerRegistration.sync.register('sync-dashboard-data');
                }
                catch (error) {
                    logger.error('⚠️ Failed to register dashboard sync:', error, {});
                }
            }
            return {
                success: true,
                syncedCount: result.success,
                failedCount: result.failed,
                heldCount: result.held,
                conflicts: []
            };
        }
        catch (error) {
            logger.error('❌ Sync failed:', error, {});
            this.notifyListeners('failed', { error });
            return {
                success: false,
                syncedCount: 0,
                failedCount: 0,
                heldCount: 0,
                conflicts: []
            };
        }
        finally {
            this.syncInProgress = false;
        }
    }
    /**
     * Process a specific retry request
     */
    private async processRetryRequest(requestId: string): Promise<void> {
        logger.api('🔄 Processing retry request:', requestId);
        // This will be handled by the general sync process
        await this.sync();
    }
    /**
     * Force sync with service worker
     */
    public async forceSyncWithServiceWorker(): Promise<void> {
        if (!this.serviceWorkerRegistration) {
            logger.warn('⚠️ Service Worker not available');
            return;
        }
        try {
            // Send message to service worker to force sync
            if (navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                    type: 'FORCE_SYNC'
                });
                logger.debug('📤 Sent force sync message to service worker');
            }
            // Also register background sync
            if ('sync' in this.serviceWorkerRegistration) {
                await this.serviceWorkerRegistration.sync.register('sync-dashboard-data');
                logger.debug('🔄 Registered background sync');
            }
        }
        catch (error) {
            logger.error('❌ Failed to force sync:', error, {});
        }
    }
    // =============================================================================
    // AUTO-SYNC MANAGEMENT
    // =============================================================================
    /**
     * Start automatic periodic sync
     */
    public startAutoSync(intervalMs: number = 5 * 60 * 1000): void {
        if (this.autoSyncInterval) {
            logger.warn('⚠️ Auto-sync already running');
            return;
        }
        this.autoSyncInterval = setInterval(async () => {
            if (navigator.onLine) {
                const pendingCount = await this.getPendingCount();
                if (pendingCount > 0) {
                    logger.debug(`🔄 Auto-sync: ${pendingCount} pending requests`);
                    await this.sync({ actorUserId: this.currentActorUserId });
                }
            }
        }, intervalMs);
        logger.debug(`✅ Auto-sync started (interval: ${intervalMs}ms)`);
    }
    /**
     * Stop automatic periodic sync
     */
    public stopAutoSync(): void {
        if (this.autoSyncInterval) {
            clearInterval(this.autoSyncInterval);
            this.autoSyncInterval = null;
            logger.debug('🛑 Auto-sync stopped');
        }
    }
    // =============================================================================
    // ONLINE/OFFLINE HANDLING
    // =============================================================================
    /**
     * Handle online event.
     *
     * IMPORTANT: We deliberately do NOT call `sync()` here. The auth
     * context owns reconnect ordering — it must revalidate `/api/auth/me`
     * first, then call `sync({ actorUserId })`. Flushing the queue from
     * here would race revalidation and risk replaying mutations either
     * unauthenticated or under a stale identity.
     *
     * If `deferAutoSyncUntilAuthVerified` is disabled (legacy / non-K9
     * surfaces that opt out of the offline auth gating), we fall back to
     * the previous behavior to preserve existing flows.
     */
    private async handleOnline(): Promise<void> {
        logger.debug('🌐 Connection restored');
        if (this.deferAutoSyncUntilAuthVerified) {
            logger.debug(
                '⏸  Holding sync until auth context confirms user via /api/auth/me',
            );
            return;
        }
        setTimeout(async () => {
            const pendingCount = await this.getPendingCount();
            if (pendingCount > 0) {
                await this.sync({ actorUserId: this.currentActorUserId });
            }
        }, 1000);
    }
    /**
     * Handle offline event
     */
    private handleOffline(): void {
        logger.debug('📵 Connection lost, requests will be queued for sync');
    }
    // =============================================================================
    // EVENT LISTENERS
    // =============================================================================
    /**
     * Add sync event listener
     */
    public addEventListener(listener: SyncEventListener): void {
        this.listeners.add(listener);
    }
    /**
     * Remove sync event listener
     */
    public removeEventListener(listener: SyncEventListener): void {
        this.listeners.delete(listener);
    }
    /**
     * Notify all listeners
     */
    private notifyListeners(event: SyncEventType, data?: any): void {
        this.listeners.forEach(listener => {
            try {
                listener(event, data);
            }
            catch (error) {
                logger.error('❌ Error in sync listener:', error, {});
            }
        });
    }
    // =============================================================================
    // CONFLICT RESOLUTION
    // =============================================================================
    /**
     * Handle sync conflicts
     */
    private async handleConflict(requestId: string, localData: any, serverData: any): Promise<'use_local' | 'use_server' | 'merge'> {
        // Simple conflict resolution: use server data by default
        // This can be enhanced with user prompts or smart merging
        logger.warn('⚠️ Sync conflict detected:', { "arg0": { requestId, localData, serverData } });
        this.notifyListeners('conflict', {
            requestId,
            localData,
            serverData
        });
        // Default strategy: use server data
        return 'use_server';
    }
    // =============================================================================
    // SPECIALIZED SYNC OPERATIONS
    // =============================================================================
    /**
     * Queue form submission for sync
     */
    public async queueFormSubmission(url: string, formData: any, headers?: Record<string, string>): Promise<string> {
        return this.queueRequest(url, 'POST', formData, {
            priority: 'high',
            type: 'form_submission',
            headers
        });
    }
    /**
     * Queue profile update for sync
     */
    public async queueProfileUpdate(url: string, profileData: any, headers?: Record<string, string>): Promise<string> {
        return this.queueRequest(url, 'PUT', profileData, {
            priority: 'high',
            type: 'profile_update',
            headers
        });
    }
    /**
     * Queue event registration for sync
     */
    public async queueEventRegistration(url: string, registrationData: any, headers?: Record<string, string>): Promise<string> {
        return this.queueRequest(url, 'POST', registrationData, {
            priority: 'high',
            type: 'event_registration',
            headers
        });
    }
    /**
     * Queue notification read status for sync
     */
    public async queueNotificationRead(notificationId: number, headers?: Record<string, string>): Promise<string> {
        return this.queueRequest(`/api/notifications/${notificationId}/read`, 'PUT', { read: true }, {
            priority: 'low',
            type: 'notification_read',
            headers
        });
    }
    /**
     * Queue analytics event for sync
     */
    public async queueAnalyticsEvent(eventData: any, headers?: Record<string, string>): Promise<string> {
        return this.queueRequest('/api/analytics/event', 'POST', eventData, {
            priority: 'low',
            type: 'analytics_event',
            headers
        });
    }
    // =============================================================================
    // UTILITY METHODS
    // =============================================================================
    /**
     * Check if sync is in progress
     */
    public isSyncing(): boolean {
        return this.syncInProgress;
    }
    /**
     * Check if online
     */
    public isOnline(): boolean {
        return navigator.onLine;
    }
    /**
     * Get sync statistics
     */
    public async getStats(): Promise<{
        pendingRequests: number;
        isOnline: boolean;
        isSyncing: boolean;
        autoSyncEnabled: boolean;
    }> {
        const pendingRequests = await this.getPendingCount();
        return {
            pendingRequests,
            isOnline: this.isOnline(),
            isSyncing: this.isSyncing(),
            autoSyncEnabled: this.autoSyncInterval !== null
        };
    }
}
// Export singleton instance
export const backgroundSyncService = BackgroundSyncService.getInstance();
export default backgroundSyncService;
